import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ClipboardList, Plus, Search, MoreHorizontal, Pencil, Trash2, CheckCircle2, FileDown, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function ReportsPage() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState({ title: '', template_id: '', job_id: '', project_id: '', type: 'custom', data: {}, notes: '' });
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', type: 'custom', fields: [] });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const [rRes, tRes, jRes] = await Promise.all([
        api.get('/reports', { params }),
        api.get('/reports/templates'),
        api.get('/jobs', { params: { page_size: 500 } })
      ]);
      setReports(rRes.data);
      setTemplates(tRes.data);
      setJobs(jRes.data.items || jRes.data);
    } catch { toast.error(t('reports.errorLoading')); }
    finally { setLoading(false); }
  }, [search, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateFromTemplate = (template) => {
    setEditing(null);
    setSelectedTemplate(template);
    const initialData = {};
    (template.fields || []).forEach(f => { initialData[f.key] = ''; });
    setForm({ title: `${template.name}`, template_id: template.id, job_id: '', project_id: '', type: template.type, data: initialData, notes: '' });
    setDialogOpen(true);
  };

  const openCreateBlank = () => {
    setEditing(null);
    setSelectedTemplate(null);
    setForm({ title: '', template_id: '', job_id: '', project_id: '', type: 'custom', data: {}, notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (report) => {
    setEditing(report);
    const template = templates.find(tp => tp.id === report.template_id) || null;
    setSelectedTemplate(template);
    setForm({
      title: report.title, template_id: report.template_id || '', job_id: report.job_id || '',
      project_id: report.project_id || '', type: report.type, data: report.data || {}, notes: report.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error(t('reports.titleRequired')); return; }
    try {
      if (editing) {
        await api.put(`/reports/${editing.id}`, { title: form.title, data: form.data, notes: form.notes });
        toast.success(t('reports.reportUpdated'));
      } else {
        await api.post('/reports', form);
        toast.success(t('reports.reportCreated'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('reports.errorSaving')); }
  };

  const handleFinalize = async (id) => {
    try { await api.post(`/reports/${id}/finalize`); toast.success(t('reports.reportFinalized')); fetchData(); }
    catch { toast.error(t('reports.errorFinalize')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/reports/${id}`); toast.success(t('reports.reportDeleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const downloadPDF = async (report) => {
    try {
      const res = await api.get(`/pdf/report/${report.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_${report.report_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('reports.downloadPdf'));
    } catch { toast.error(t('reports.errorDownload')); }
  };

  const openTemplateDialog = () => {
    setTemplateForm({ name: '', description: '', type: 'custom', fields: [{ key: '', label: '', type: 'text', required: false }] });
    setTemplateDialogOpen(true);
  };

  const addField = () => setTemplateForm({ ...templateForm, fields: [...templateForm.fields, { key: '', label: '', type: 'text', required: false }] });
  const removeField = (idx) => setTemplateForm({ ...templateForm, fields: templateForm.fields.filter((_, i) => i !== idx) });
  const updateField = (idx, field, val) => {
    const fields = [...templateForm.fields];
    fields[idx] = { ...fields[idx], [field]: val };
    if (field === 'label' && !fields[idx].key) {
      fields[idx].key = val.toLowerCase().replace(/[^a-z0-9]/g, '_');
    }
    setTemplateForm({ ...templateForm, fields });
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      await api.post('/reports/templates', templateForm);
      toast.success(t('reports.templateCreated'));
      setTemplateDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('reports.errorSaving')); }
  };

  const handleDeleteTemplate = async (id) => {
    try { await api.delete(`/reports/templates/${id}`); toast.success(t('reports.templateDeleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const updateFormData = (key, value) => {
    setForm({ ...form, data: { ...form.data, [key]: value } });
  };

  const getJobNumber = (id) => { const j = jobs.find(j => j.id === id); return j ? j.job_number : '-'; };

  const downloadCSV = async (resource, label) => {
    try {
      const res = await api.get(`/export/${resource}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${resource}_bluewell.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} exportados`);
    } catch { toast.error(t('errors.generic')); }
  };

  return (
    <div data-testid="reports-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('reports.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('reports.totalReports', { count: reports.length })} | {t('reports.totalTemplates', { count: templates.length })}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="new-template-btn" variant="outline" onClick={openTemplateDialog} className="border-[#003366]/20 text-[#003366]">
            <Plus className="w-4 h-4 mr-2" /> {t('reports.newTemplate')}
          </Button>
          <Button data-testid="new-report-btn" onClick={openCreateBlank} className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
            <Plus className="w-4 h-4 mr-2" /> {t('reports.newReport')}
          </Button>
        </div>
      </div>

      <Card className="bg-white border-gray-100 shadow-sm">
        <CardContent className="p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-[#3399FF]" /> {t('reports.exportSection')}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="export-invoices-csv" variant="outline" size="sm" onClick={() => downloadCSV('invoices', t('reports.exportInvoices'))} className="text-xs border-[#003366]/15 text-[#003366] hover:bg-[#003366]/5">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {t('reports.exportInvoices')}
            </Button>
            <Button data-testid="export-projects-csv" variant="outline" size="sm" onClick={() => downloadCSV('projects', t('reports.exportProjects'))} className="text-xs border-[#003366]/15 text-[#003366] hover:bg-[#003366]/5">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {t('reports.exportProjects')}
            </Button>
            <Button data-testid="export-jobs-csv" variant="outline" size="sm" onClick={() => downloadCSV('jobs', t('reports.exportJobs'))} className="text-xs border-[#003366]/15 text-[#003366] hover:bg-[#003366]/5">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {t('reports.exportJobs')}
            </Button>
            <Button data-testid="export-contacts-csv" variant="outline" size="sm" onClick={() => downloadCSV('contacts', t('reports.exportContacts'))} className="text-xs border-[#003366]/15 text-[#003366] hover:bg-[#003366]/5">
              <FileDown className="w-3.5 h-3.5 mr-1.5" /> {t('reports.exportContacts')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {templates.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3">{t('reports.availableTemplates')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(tp => (
              <Card key={tp.id} className="bg-white border-gray-100 hover:border-[#3399FF]/30 transition-colors cursor-pointer group" onClick={() => openCreateFromTemplate(tp)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-[#003366]">{tp.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{tp.description || `${(tp.fields || []).length} ${t('reports.addField').toLowerCase()}s`}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"><MoreHorizontal className="w-3.5 h-3.5" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(tp.id); }} className="text-red-600">
                          <Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input data-testid="search-reports" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('reports.searchPlaceholder')} className="pl-10 h-9 bg-white" />
      </div>

      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : reports.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('reports.noReports')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('reports.createFirstReport')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">#</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('reports.reportTitle')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('common.type')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 hidden md:table-cell">{t('reports.reportJob')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('bookings.tableHeaders.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5 text-sm font-mono text-gray-500">{r.report_number}</td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-medium text-gray-900">{r.title}</p>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell capitalize">
                        {r.type === 'well_completion' ? t('reports.wellCompletion') : r.type}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600 hidden md:table-cell">{r.job_id ? getJobNumber(r.job_id) : '-'}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-3.5 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {r.status === 'draft' && <DropdownMenuItem onClick={() => openEdit(r)}><Pencil className="w-3.5 h-3.5 mr-2" />{t('common.edit')}</DropdownMenuItem>}
                            {r.status === 'draft' && <DropdownMenuItem onClick={() => handleFinalize(r.id)}><CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-500" />{t('reports.finalize')}</DropdownMenuItem>}
                            <DropdownMenuItem data-testid={`download-report-pdf-${r.id}`} onClick={() => downloadPDF(r)}><FileDown className="w-3.5 h-3.5 mr-2 text-blue-500" />{t('reports.exportPdf')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(r.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('reports.editReport') : t('reports.newReport')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('reports.reportTitle')} *</Label>
                <Input data-testid="report-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={t('reports.reportTitlePlaceholder')} />
              </div>
              {!editing && (
                <div className="space-y-2">
                  <Label>{t('reports.reportJob')}</Label>
                  <Select value={form.job_id} onValueChange={v => setForm({ ...form, job_id: v })}>
                    <SelectTrigger><SelectValue placeholder={t('reports.linkJob')} /></SelectTrigger>
                    <SelectContent>
                      {jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.job_number} - {j.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {selectedTemplate && selectedTemplate.fields && selectedTemplate.fields.length > 0 && (
              <div className="space-y-4">
                <p className="text-sm font-semibold text-gray-700 border-b pb-2">{t('reports.templateFields')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedTemplate.fields.map((field) => (
                    <div key={field.key} className="space-y-2">
                      <Label>{field.label} {field.required && '*'}</Label>
                      {field.type === 'textarea' ? (
                        <Textarea
                          value={form.data[field.key] || ''}
                          onChange={e => updateFormData(field.key, e.target.value)}
                          placeholder={field.label}
                          rows={3}
                        />
                      ) : field.type === 'select' ? (
                        <Select value={form.data[field.key] || ''} onValueChange={v => updateFormData(field.key, v)}>
                          <SelectTrigger><SelectValue placeholder={t('common.select')} /></SelectTrigger>
                          <SelectContent>
                            {(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                          value={form.data[field.key] || ''}
                          onChange={e => updateFormData(field.key, e.target.value)}
                          placeholder={field.label}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-report-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('reports.saveReport') : t('reports.createReport')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{t('reports.newTemplate')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('reports.templateName')} *</Label>
                <Input data-testid="template-name-input" value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder={t('reports.templateNamePlaceholder')} />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>{t('reports.templateDescription')}</Label>
                <Textarea value={templateForm.description} onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })} placeholder={t('reports.templateDescriptionPlaceholder')} rows={2} />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">{t('reports.templateFields')}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField} data-testid="add-template-field-btn">
                  <Plus className="w-3 h-3 mr-1" /> {t('reports.addField')}
                </Button>
              </div>
              <div className="space-y-2">
                {templateForm.fields.map((field, idx) => (
                  <div key={idx} className="flex gap-2 items-center p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <Input value={field.label} onChange={e => updateField(idx, 'label', e.target.value)} placeholder={t('reports.fieldLabel')} className="flex-1 bg-white h-9" />
                    <Select value={field.type} onValueChange={v => updateField(idx, 'type', v)}>
                      <SelectTrigger className="w-32 h-9 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">{t('lineItems.fields.text')}</SelectItem>
                        <SelectItem value="number">{t('lineItems.fields.number')}</SelectItem>
                        <SelectItem value="date">{t('timecards.date')}</SelectItem>
                        <SelectItem value="textarea">{t('lineItems.fields.textarea')}</SelectItem>
                        <SelectItem value="select">{t('lineItems.fields.select')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      <input type="checkbox" checked={field.required || false} onChange={e => updateField(idx, 'required', e.target.checked)} /> {t('reports.fieldRequired')}
                    </label>
                    {templateForm.fields.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeField(idx)} className="h-9 w-9 text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-template-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">{t('reports.saveTemplate')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
